import type { EChartsType } from 'echarts';
import * as echarts from 'echarts';
import { useEffect, useRef } from 'react';
import { PROJECTION_THEME_NAME, registerProjectionTheme } from './theme';

/**
 * The shared ECharts React wrapper. Owns the full lifecycle so every chart component (fan chart,
 * histogram, stress comparison) is a thin "compute the option" function over THIS:
 *  - registers + uses the ONE shared `projection` theme (theme.ts);
 *  - `init` on mount, `setOption(option, { notMerge: replaceMerge })` on option change;
 *  - a `ResizeObserver` keeps the canvas crisp on container resize;
 *  - `dispose` on unmount (no leaks);
 *  - the chart "draw-in" animation comes from the option (`animation: true`, the restrained spec).
 *
 * Pass a memoized `option` (the EChartsOption) so the chart only re-renders when data changes.
 */
export interface EChartProps {
  /** The full ECharts option object (built by the specific chart component). */
  option: echarts.EChartsCoreOption;
  /** Height of the chart box (px or any CSS length). Width fills the parent. */
  height?: number | string;
  /** Replace (notMerge) the whole option on update instead of deep-merging. Default true. */
  replace?: boolean;
  className?: string;
  'aria-label'?: string;
  /** Called once the chart instance exists (for imperative needs like dispatchAction). */
  onInit?: (chart: EChartsType) => void;
}

export function EChart({
  option,
  height = 320,
  replace = true,
  className,
  onInit,
  ...rest
}: EChartProps): React.ReactElement {
  const elRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<EChartsType | null>(null);

  // Init / dispose once. `onInit` is intentionally not a dependency — the chart is created a single
  // time and `onInit` only needs the freshly-created instance.
  // biome-ignore lint/correctness/useExhaustiveDependencies: init runs once; onInit excluded by design.
  useEffect(() => {
    if (elRef.current === null) return;
    registerProjectionTheme();
    const chart = echarts.init(elRef.current, PROJECTION_THEME_NAME, { renderer: 'canvas' });
    chartRef.current = chart;
    onInit?.(chart);
    const ro = new ResizeObserver(() => chart.resize());
    ro.observe(elRef.current);
    return () => {
      ro.disconnect();
      chart.dispose();
      chartRef.current = null;
    };
  }, []);

  // Apply option on change.
  useEffect(() => {
    chartRef.current?.setOption(option, { notMerge: replace });
  }, [option, replace]);

  return (
    <div
      ref={elRef}
      className={className}
      role="img"
      aria-label={rest['aria-label']}
      style={{ width: '100%', height }}
    />
  );
}
